import { useDisclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { LoadCarrierListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { BookLoadCarrierTransactionModal } from "@/modals/stock/book-load-carrier-transaction-modal";
import { EditLoadCarrierModal } from "@/modals/stock/edit-load-carrier-modal";

export function RowActions({ cell }: CellContext<LoadCarrierListItem, "actions">) {
	/** Hooks */
	const { t } = useTranslation();
	const editModal = useDisclosure();
	const bookModal = useDisclosure();
	const asyncModal = useAsyncModal();

	/** Context */
	const { refetch } = useDataTableContext();

	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	// The same three-state guard `apps/web/src/components/citadel/can.tsx` documents: the menu is
	// an array rather than a subtree, so the gate sits here, and it is dropped only on a definitive
	// denial — while the rules load the server keeps the last word. Editing and booking both write,
	// so both hang off the `update Stock` their routes gate on; deleting is its own rule.
	const stockUpdateIsDenied = !!citadel && citadel.cannot("update", "Stock");
	const stockDeleteIsDenied = !!citadel && citadel.cannot("delete", "Stock");

	/** Functions */
	// Confirmed, because the cascade takes the carrier's whole ledger with it — every arrival,
	// return and correction that ever explained its balance.
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.stock["load-carriers"][":loadCarrierId"].$delete({
			param: { loadCarrierId: cell.row.original.id },
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={`/organization/data/stock/load-carrier/${cell.row.original.id}`}>
				{!stockUpdateIsDenied && (
					<Menu
						items={createStandardMenuItems({
							onEdit: editModal.onOpen,
							onDelete: stockDeleteIsDenied ? undefined : handleOnDelete,
							items: [
								{
									label: t("stock.book_load_carrier_movement"),
									onClick: bookModal.onOpen,
									icon: icon("ArrowLeftRight"),
								},
							],
						})}
					>
						<Menu.Button />
					</Menu>
				)}
			</RowActionsWrapper>
			{editModal.isOpen && (
				<EditLoadCarrierModal
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
					onComplete={refetch}
					loadCarrierId={cell.row.original.id}
				/>
			)}
			{bookModal.isOpen && (
				<BookLoadCarrierTransactionModal
					isOpen={bookModal.isOpen}
					onClose={bookModal.onClose}
					onComplete={refetch}
					loadCarrierId={cell.row.original.id}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					message={t("stock.delete_load_carrier_confirmation")}
					controller={asyncModal}
				/>
			)}
		</Fragment>
	);
}
